import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
	User,
	Package,
	LogOut,
	Edit,
	Trash2,
	LayoutDashboard,
	ShoppingBag,
	Clock3,
	CheckCircle2,
	ChevronRight,
	Mail,
	Phone,
	CalendarDays,
	ReceiptText,
	CircleUserRound,
} from "lucide-react";

import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "../components/ui/tabs";

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "../components/ui/dialog";

import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { supabase } from "../lib/supabase";

export default function AccountPage() {
	const { user, profile, isAuthenticated, signOut, updateProfile } = useAuth();

	const navigate = useNavigate();

	const [isEditing, setIsEditing] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [deleteConfirmText, setDeleteConfirmText] = useState("");
	const [isDeleting, setIsDeleting] = useState(false);

	const [orders, setOrders] = useState([]);
	const [ordersLoading, setOrdersLoading] = useState(true);
	const [activeTab, setActiveTab] = useState("overview");

	const [formData, setFormData] = useState({
		firstName: "",
		lastName: "",
		email: "",
		phone: "",
	});

	// ─────────────────────────────────────────────────────────────
	// Profil
	// ─────────────────────────────────────────────────────────────

	useEffect(() => {
		setFormData({
			firstName: profile?.first_name || "",
			lastName: profile?.last_name || "",
			email: user?.email || "",
			phone: profile?.phone || "",
		});
	}, [profile, user]);

	// ─────────────────────────────────────────────────────────────
	// Commandes
	// ─────────────────────────────────────────────────────────────

	useEffect(() => {
		if (!isAuthenticated) return;
		if (!user?.id) return;

		const loadOrders = async () => {
			try {
				setOrdersLoading(true);

				const { data, error } = await supabase
					.from("orders")
					.select(`*, order_items (*)`)
					.eq("user_id", user.id)
					.order("created_at", { ascending: false });

				if (error) throw error;

				setOrders(data || []);
			} catch (error) {
				console.error("Erreur chargement commandes:", error);
				toast.error("Impossible de charger vos commandes");
				setOrders([]);
			} finally {
				setOrdersLoading(false);
			}
		};

		loadOrders();
	}, [isAuthenticated, user?.id]);

	// ─────────────────────────────────────────────────────────────
	// Protection page
	// ─────────────────────────────────────────────────────────────

	useEffect(() => {
		if (!isAuthenticated) {
			navigate("/connexion", {
				state: {
					from: {
						pathname: "/mon-compte",
					},
				},
				replace: true,
			});
		}
	}, [isAuthenticated, navigate]);

	// ─────────────────────────────────────────────────────────────
	// Modification profil
	// ─────────────────────────────────────────────────────────────

	const handleInputChange = (e) => {
		const { name, value } = e.target;

		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const handleUpdateProfile = async (e) => {
		e.preventDefault();
		setIsLoading(true);

		try {
			const result = await updateProfile({
				first_name: formData.firstName,
				last_name: formData.lastName,
				phone: formData.phone,
			});

			if (result.success) {
				toast.success("Profil mis à jour");
				setIsEditing(false);
			} else {
				toast.error(result.error || "Erreur lors de la mise à jour");
			}
		} catch (error) {
			toast.error("Une erreur est survenue");
		} finally {
			setIsLoading(false);
		}
	};

	const handlePasswordChangeRequest = async () => {
		if (!user?.email) {
			toast.error("Adresse email introuvable");
			return;
		}

		try {
			const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
				redirectTo: `${window.location.origin}/reset-password`,
			});

			if (error) throw error;

			toast.success(
				"Un email vous a été envoyé pour modifier votre mot de passe.",
			);
		} catch (error) {
			console.error("Erreur changement mot de passe:", error);

			toast.error(
				"Impossible d'envoyer l'email de modification du mot de passe.",
			);
		}
	};

	// ─────────────────────────────────────────────────────────────
	// Déconnexion
	// ─────────────────────────────────────────────────────────────

	const handleSignOut = async () => {
		await signOut();
		toast.success("Déconnexion réussie");
		navigate("/");
	};

	// ─────────────────────────────────────────────────────────────
	// Suppression compte
	// ─────────────────────────────────────────────────────────────

	const handleDeleteAccount = async () => {
		if (deleteConfirmText !== "SUPPRIMER") {
			toast.error("Veuillez taper SUPPRIMER pour confirmer");
			return;
		}

		setIsDeleting(true);

		try {
			const { data, error } = await supabase.functions.invoke("delete-account");

			if (error) throw error;

			if (!data?.success) {
				toast.error(data?.message || "Erreur lors de la suppression");
				return;
			}

			await signOut();

			toast.success("Votre compte a été supprimé");
			navigate("/");
		} catch (error) {
			console.error("Erreur suppression compte:", error);

			toast.error("Une erreur est survenue lors de la suppression");
		} finally {
			setIsDeleting(false);
			setDeleteModalOpen(false);
		}
	};

	// ─────────────────────────────────────────────────────────────
	// Helpers
	// ─────────────────────────────────────────────────────────────

	const formatPrice = (price) => {
		return new Intl.NumberFormat("fr-CH", {
			style: "currency",
			currency: "CHF",
		}).format(Number(price || 0));
	};

	const formatDate = (dateStr) => {
		if (!dateStr) return "-";

		return new Date(dateStr).toLocaleDateString("fr-CH", {
			day: "numeric",
			month: "long",
			year: "numeric",
		});
	};

	const getStatusLabel = (status) => {
		const labels = {
			pending: "En attente",
			confirmed: "Confirmée",
			preparing: "En préparation",
			ready: "Prête",
			completed: "Terminée",
			cancelled: "Annulée",
			processing: "En préparation",
		};

		return labels[status] || status || "En attente";
	};

	const getStatusColor = (status) => {
		const colors = {
			pending: "bg-amber-50 text-amber-700 border-amber-200",
			confirmed: "bg-blue-50 text-blue-700 border-blue-200",
			preparing: "bg-indigo-50 text-indigo-700 border-indigo-200",
			ready: "bg-purple-50 text-purple-700 border-purple-200",
			completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
			cancelled: "bg-red-50 text-red-700 border-red-200",
			processing: "bg-blue-50 text-blue-700 border-blue-200",
		};

		return colors[status] || "bg-gray-50 text-gray-700 border-gray-200";
	};

	// ─────────────────────────────────────────────────────────────
	// Données du dashboard
	// ─────────────────────────────────────────────────────────────

	const accountStats = useMemo(() => {
		const totalOrders = orders.length;

		const completedOrders = orders.filter(
			(order) => order.status === "completed",
		).length;

		const activeOrders = orders.filter((order) =>
			["pending", "confirmed", "preparing", "ready", "processing"].includes(
				order.status,
			),
		).length;

		const totalSpent = orders
			.filter((order) => order.status !== "cancelled")
			.reduce((total, order) => total + Number(order.total_amount || 0), 0);

		return {
			totalOrders,
			completedOrders,
			activeOrders,
			totalSpent,
		};
	}, [orders]);

	const latestOrder = orders.length > 0 ? orders[0] : null;

	const displayName =
		profile?.first_name || user?.email?.split("@")[0] || "client";

	const initials = `${profile?.first_name?.[0] || ""}${
		profile?.last_name?.[0] || ""
	}`.toUpperCase();

	// ─────────────────────────────────────────────────────────────
	// UI
	// ─────────────────────────────────────────────────────────────

	return (
		<>
			<main
				data-testid="account-page"
				className="min-h-screen pt-20 bg-warm-alabaster"
			>
				{/* ─────────────────────────────────────────────── */}
				{/* HERO */}
				{/* ─────────────────────────────────────────────── */}

				<section className="border-b border-border-subtle bg-warm-secondary">
					<div className="max-w-7xl mx-auto px-6 lg:px-12 py-10 lg:py-14">
						<div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
							<div className="flex items-center gap-5">
								<div className="w-16 h-16 lg:w-20 lg:h-20 rounded-full bg-espresso text-warm-alabaster flex items-center justify-center flex-shrink-0 shadow-sm">
									{initials ? (
										<span className="font-serif text-xl lg:text-2xl font-semibold">
											{initials}
										</span>
									) : (
										<User className="w-7 h-7" />
									)}
								</div>

								<div>
									<p className="text-xs uppercase tracking-[0.18em] text-latte-gold font-semibold mb-2">
										Espace client
									</p>

									<h1 className="font-serif text-3xl lg:text-4xl text-espresso font-semibold">
										Bonjour, {displayName}
									</h1>

									<p className="text-soft-charcoal-secondary mt-2 max-w-xl">
										Retrouvez vos commandes et gérez vos informations
										personnelles depuis votre espace client.
									</p>
								</div>
							</div>

							<Button
								variant="ghost"
								onClick={handleSignOut}
								className="self-start lg:self-auto border border-border-subtle bg-white hover:bg-red-50 hover:text-red-600"
								data-testid="logout-btn"
							>
								<LogOut className="w-4 h-4 mr-2" />
								Se déconnecter
							</Button>
						</div>
					</div>
				</section>

				{/* ─────────────────────────────────────────────── */}
				{/* CONTENT */}
				{/* ─────────────────────────────────────────────── */}

				<section className="py-8 lg:py-12">
					<div className="max-w-7xl mx-auto px-6 lg:px-12">
						<Tabs
							value={activeTab}
							onValueChange={setActiveTab}
							className="grid lg:grid-cols-[240px_minmax(0,1fr)] gap-8 lg:gap-12"
						>
							{/* Navigation compte */}

							<aside>
								<div className="lg:sticky lg:top-28">
									<TabsList className="w-full h-auto p-1.5 lg:p-2 bg-white border border-border-subtle rounded-xl flex lg:flex-col justify-start gap-1 overflow-x-auto">
										<TabsTrigger
											value="overview"
											className="min-w-max lg:w-full justify-start gap-3 rounded-lg px-4 py-3 text-soft-charcoal-secondary data-[state=active]:bg-warm-secondary data-[state=active]:text-espresso data-[state=active]:shadow-none"
										>
											<LayoutDashboard className="w-4 h-4" />
											Vue d'ensemble
										</TabsTrigger>

										<TabsTrigger
											value="orders"
											className="min-w-max lg:w-full justify-start gap-3 rounded-lg px-4 py-3 text-soft-charcoal-secondary data-[state=active]:bg-warm-secondary data-[state=active]:text-espresso data-[state=active]:shadow-none"
											data-testid="tab-orders"
										>
											<Package className="w-4 h-4" />
											Mes commandes
											{accountStats.activeOrders > 0 && (
												<span className="ml-auto min-w-5 h-5 px-1.5 rounded-full bg-latte-gold text-white text-[10px] flex items-center justify-center">
													{accountStats.activeOrders}
												</span>
											)}
										</TabsTrigger>

										<TabsTrigger
											value="profile"
											className="min-w-max lg:w-full justify-start gap-3 rounded-lg px-4 py-3 text-soft-charcoal-secondary data-[state=active]:bg-warm-secondary data-[state=active]:text-espresso data-[state=active]:shadow-none"
											data-testid="tab-profile"
										>
											<CircleUserRound className="w-4 h-4" />
											Mon profil
										</TabsTrigger>
									</TabsList>

									<div className="hidden lg:block mt-6 px-4">
										<p className="text-xs text-soft-charcoal-secondary leading-relaxed">
											Connecté avec
										</p>

										<p className="text-xs text-espresso font-medium mt-1 break-all">
											{user?.email}
										</p>
									</div>
								</div>
							</aside>

							{/* ─────────────────────────────────── */}
							{/* VUE D'ENSEMBLE */}
							{/* ─────────────────────────────────── */}

							<TabsContent
								value="overview"
								className="mt-0 focus-visible:outline-none"
							>
								<div className="space-y-8">
									<div>
										<p className="text-xs uppercase tracking-[0.16em] text-latte-gold font-semibold mb-2">
											Tableau de bord
										</p>

										<h2 className="font-serif text-2xl lg:text-3xl font-semibold text-espresso">
											Vue d'ensemble
										</h2>

										<p className="text-soft-charcoal-secondary mt-2">
											Un aperçu rapide de votre activité et de votre compte.
										</p>
									</div>

									{/* Statistiques */}

									<div className="grid grid-cols-2 xl:grid-cols-4 gap-3 lg:gap-4">
										<StatCard
											icon={ShoppingBag}
											label="Commandes"
											value={ordersLoading ? "—" : accountStats.totalOrders}
										/>

										<StatCard
											icon={Clock3}
											label="En cours"
											value={ordersLoading ? "—" : accountStats.activeOrders}
										/>

										<StatCard
											icon={CheckCircle2}
											label="Terminées"
											value={ordersLoading ? "—" : accountStats.completedOrders}
										/>

										<StatCard
											icon={ReceiptText}
											label="Total"
											value={
												ordersLoading
													? "—"
													: formatPrice(accountStats.totalSpent)
											}
											compact
										/>
									</div>

									{/* Dernière commande */}

									<div>
										<div className="flex items-end justify-between gap-4 mb-4">
											<div>
												<h3 className="font-serif text-xl font-semibold text-espresso">
													Dernière commande
												</h3>

												<p className="text-sm text-soft-charcoal-secondary mt-1">
													Suivez votre achat le plus récent.
												</p>
											</div>

											{orders.length > 0 && (
												<button
													type="button"
													onClick={() => setActiveTab("orders")}
													className="p-0 h-auto bg-transparent text-sm text-latte-gold hover:text-latte-gold-secondary inline-flex items-center"
												>
													Tout voir
													<ChevronRight className="w-4 h-4 ml-1" />
												</button>
											)}
										</div>

										{ordersLoading ? (
											<LoadingCard text="Chargement de votre dernière commande..." />
										) : latestOrder ? (
											<OrderCard
												order={latestOrder}
												formatPrice={formatPrice}
												formatDate={formatDate}
												getStatusLabel={getStatusLabel}
												getStatusColor={getStatusColor}
												featured
											/>
										) : (
											<EmptyOrders />
										)}
									</div>

									{/* Profil rapide */}

									<div>
										<h3 className="font-serif text-xl font-semibold text-espresso mb-4">
											Mes informations
										</h3>

										<div className="bg-white border border-border-subtle rounded-xl overflow-hidden">
											<div className="p-6 lg:p-7 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
												<div className="flex items-center gap-4">
													<div className="w-12 h-12 rounded-full bg-warm-secondary flex items-center justify-center text-latte-gold flex-shrink-0">
														<User className="w-5 h-5" />
													</div>

													<div>
														<p className="font-serif text-lg font-semibold text-espresso">
															{profile?.first_name || profile?.last_name
																? `${profile?.first_name || ""} ${profile?.last_name || ""}`.trim()
																: "Informations personnelles"}
														</p>

														<p className="text-sm text-soft-charcoal-secondary mt-1">
															{user?.email}
														</p>

														{profile?.phone && (
															<p className="text-sm text-soft-charcoal-secondary mt-0.5">
																{profile.phone}
															</p>
														)}
													</div>
												</div>

												<button
													type="button"
													onClick={() => setActiveTab("profile")}
													className="h-auto bg-transparent p-0"
												>
													<span className="inline-flex items-center px-4 py-2.5 rounded-lg border border-border-subtle text-sm font-medium text-espresso hover:bg-warm-secondary transition-colors">
														<Edit className="w-4 h-4 mr-2" />
														Gérer mon profil
													</span>
												</button>
											</div>
										</div>
									</div>
								</div>
							</TabsContent>

							{/* ─────────────────────────────────── */}
							{/* COMMANDES */}
							{/* ─────────────────────────────────── */}

							<TabsContent
								value="orders"
								className="mt-0 focus-visible:outline-none"
							>
								<div className="space-y-6">
									<div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
										<div>
											<p className="text-xs uppercase tracking-[0.16em] text-latte-gold font-semibold mb-2">
												Historique
											</p>

											<h2 className="font-serif text-2xl lg:text-3xl font-semibold text-espresso">
												Mes commandes
											</h2>

											<p className="text-soft-charcoal-secondary mt-2">
												Retrouvez le détail et le statut de toutes vos
												commandes.
											</p>
										</div>

										{!ordersLoading && orders.length > 0 && (
											<div className="text-sm text-soft-charcoal-secondary">
												{orders.length} commande
												{orders.length !== 1 ? "s" : ""}
											</div>
										)}
									</div>

									{ordersLoading ? (
										<LoadingCard text="Chargement des commandes..." />
									) : orders.length === 0 ? (
										<EmptyOrders />
									) : (
										<div className="space-y-4">
											{orders.map((order) => (
												<OrderCard
													key={order.id}
													order={order}
													formatPrice={formatPrice}
													formatDate={formatDate}
													getStatusLabel={getStatusLabel}
													getStatusColor={getStatusColor}
												/>
											))}
										</div>
									)}
								</div>
							</TabsContent>

							{/* ─────────────────────────────────── */}
							{/* PROFIL */}
							{/* ─────────────────────────────────── */}

							<TabsContent
								value="profile"
								className="mt-0 focus-visible:outline-none"
							>
								<div className="space-y-8">
									<div>
										<p className="text-xs uppercase tracking-[0.16em] text-latte-gold font-semibold mb-2">
											Compte
										</p>

										<h2 className="font-serif text-2xl lg:text-3xl font-semibold text-espresso">
											Mon profil
										</h2>

										<p className="text-soft-charcoal-secondary mt-2">
											Consultez et modifiez vos informations personnelles.
										</p>
									</div>

									{/* Informations personnelles */}

									<div className="bg-white border border-border-subtle rounded-xl overflow-hidden">
										<div className="p-6 lg:p-8 border-b border-border-subtle flex items-center justify-between gap-4">
											<div>
												<h3 className="font-serif text-xl font-semibold text-espresso">
													Informations personnelles
												</h3>

												<p className="text-sm text-soft-charcoal-secondary mt-1">
													Informations utilisées pour votre compte client.
												</p>
											</div>

											{!isEditing && (
												<Button
													variant="ghost"
													onClick={() => setIsEditing(true)}
													className="text-latte-gold hover:text-latte-gold-secondary hover:bg-warm-secondary"
													data-testid="edit-profile-btn"
												>
													<Edit className="w-4 h-4 mr-2" />
													Modifier
												</Button>
											)}
										</div>

										<div className="p-6 lg:p-8">
											{isEditing ? (
												<form
													onSubmit={handleUpdateProfile}
													className="space-y-6"
												>
													<div className="grid sm:grid-cols-2 gap-5">
														<div className="space-y-2">
															<Label htmlFor="firstName">Prénom</Label>

															<Input
																id="firstName"
																name="firstName"
																value={formData.firstName}
																onChange={handleInputChange}
																className="input-field"
																data-testid="profile-firstname"
															/>
														</div>

														<div className="space-y-2">
															<Label htmlFor="lastName">Nom</Label>

															<Input
																id="lastName"
																name="lastName"
																value={formData.lastName}
																onChange={handleInputChange}
																className="input-field"
																data-testid="profile-lastname"
															/>
														</div>
													</div>

													<div className="space-y-2">
														<Label htmlFor="email">Adresse email</Label>

														<Input
															id="email"
															name="email"
															value={formData.email}
															disabled
															className="input-field bg-warm-secondary text-soft-charcoal-secondary"
														/>

														<p className="text-xs text-soft-charcoal-secondary">
															L'adresse email associée au compte ne peut pas
															être modifiée ici.
														</p>
													</div>

													<div className="space-y-2">
														<Label htmlFor="phone">Téléphone</Label>

														<Input
															id="phone"
															name="phone"
															value={formData.phone}
															onChange={handleInputChange}
															className="input-field"
															data-testid="profile-phone"
														/>
													</div>

													<div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
														<Button
															type="button"
															variant="ghost"
															onClick={() => {
																setFormData({
																	firstName: profile?.first_name || "",
																	lastName: profile?.last_name || "",
																	email: user?.email || "",
																	phone: profile?.phone || "",
																});

																setIsEditing(false);
															}}
															className="btn-secondary"
														>
															Annuler
														</Button>

														<Button
															type="submit"
															disabled={isLoading}
															className="btn-primary"
															data-testid="save-profile-btn"
														>
															{isLoading
																? "Enregistrement..."
																: "Enregistrer les modifications"}
														</Button>
													</div>
												</form>
											) : (
												<div className="grid md:grid-cols-2 gap-x-8 gap-y-7">
													<ProfileItem
														icon={User}
														label="Prénom"
														value={profile?.first_name || "Non renseigné"}
													/>

													<ProfileItem
														icon={User}
														label="Nom"
														value={profile?.last_name || "Non renseigné"}
													/>

													<ProfileItem
														icon={Mail}
														label="Adresse email"
														value={user?.email || "Non renseignée"}
													/>

													<ProfileItem
														icon={Phone}
														label="Téléphone"
														value={profile?.phone || "Non renseigné"}
													/>
												</div>
											)}
										</div>
									</div>

									{/* Sécurité */}
									<div className="bg-white border border-border-subtle rounded-xl p-6 lg:p-8">
										<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
											<div>
												<p className="text-xs uppercase tracking-[0.14em] text-latte-gold font-semibold mb-2">
													Sécurité
												</p>

												<h3 className="font-serif text-lg font-semibold text-espresso">
													Mot de passe
												</h3>

												<p className="text-sm text-soft-charcoal-secondary mt-1 max-w-xl">
													Modifiez votre mot de passe en toute sécurité. Un
													email de confirmation sera envoyé à votre adresse.
												</p>
											</div>

											<Button
												type="button"
												variant="ghost"
												onClick={handlePasswordChangeRequest}
												className="self-start md:self-auto border border-border-subtle hover:bg-warm-secondary"
											>
												Modifier mon mot de passe
											</Button>
										</div>
									</div>

									{/* Session */}
									<div className="bg-white border border-border-subtle rounded-xl p-6 lg:p-8">
										<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
											<div>
												<h3 className="font-serif text-lg font-semibold text-espresso">
													Session
												</h3>

												<p className="text-sm text-soft-charcoal-secondary mt-1">
													Déconnectez-vous de votre espace client sur cet
													appareil.
												</p>
											</div>

											<Button
												variant="ghost"
												onClick={handleSignOut}
												className="self-start md:self-auto border border-border-subtle hover:bg-warm-secondary"
											>
												<LogOut className="w-4 h-4 mr-2" />
												Se déconnecter
											</Button>
										</div>
									</div>

									{/* Danger zone */}

									<div className="border border-red-200 bg-red-50/40 rounded-xl p-6 lg:p-8">
										<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
											<div>
												<p className="text-xs uppercase tracking-[0.14em] text-red-500 font-semibold mb-2">
													Zone sensible
												</p>

												<h3 className="font-serif text-lg font-semibold text-espresso">
													Supprimer mon compte
												</h3>

												<p className="text-sm text-soft-charcoal-secondary mt-1 max-w-xl">
													Cette action supprime définitivement votre compte et
													vos données personnelles.
												</p>
											</div>

											<Button
												variant="ghost"
												onClick={() => {
													setDeleteConfirmText("");
													setDeleteModalOpen(true);
												}}
												className="self-start md:self-auto text-red-600 border border-red-200 hover:text-red-700 hover:bg-red-100"
												data-testid="delete-account-btn"
											>
												<Trash2 className="w-4 h-4 mr-2" />
												Supprimer mon compte
											</Button>
										</div>
									</div>
								</div>
							</TabsContent>
						</Tabs>
					</div>
				</section>
			</main>

			{/* ─────────────────────────────────────────────────── */}
			{/* MODAL SUPPRESSION */}
			{/* ─────────────────────────────────────────────────── */}

			<Dialog
				open={deleteModalOpen}
				onOpenChange={(open) => {
					if (!isDeleting) {
						setDeleteModalOpen(open);
					}
				}}
			>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle className="text-red-600">
							Supprimer mon compte
						</DialogTitle>

						<DialogDescription className="space-y-3 pt-2">
							<p>
								Cette action est <strong>irréversible</strong>. Votre compte et
								vos données personnelles seront définitivement supprimés.
							</p>

							<p className="text-sm">
								Vos commandes passées seront conservées de manière anonyme pour
								les besoins de gestion.
							</p>

							<p className="text-sm font-medium text-espresso pt-2">
								Tapez{" "}
								<span className="font-mono bg-red-50 text-red-600 px-1 rounded">
									SUPPRIMER
								</span>{" "}
								pour confirmer :
							</p>

							<Input
								value={deleteConfirmText}
								onChange={(e) => setDeleteConfirmText(e.target.value)}
								placeholder="SUPPRIMER"
								className="input-field font-mono"
								disabled={isDeleting}
							/>
						</DialogDescription>
					</DialogHeader>

					<DialogFooter className="gap-2">
						<Button
							variant="ghost"
							onClick={() => setDeleteModalOpen(false)}
							disabled={isDeleting}
							className="btn-secondary"
						>
							Annuler
						</Button>

						<Button
							onClick={handleDeleteAccount}
							disabled={isDeleting || deleteConfirmText !== "SUPPRIMER"}
							className="bg-red-600 hover:bg-red-700 text-white border-none"
						>
							{isDeleting ? "Suppression..." : "Supprimer définitivement"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}

// ─────────────────────────────────────────────────────────────────
// Stat card
// ─────────────────────────────────────────────────────────────────

function StatCard({ icon: Icon, label, value, compact = false }) {
	return (
		<div className="bg-white border border-border-subtle rounded-xl p-4 lg:p-5 min-w-0">
			<div className="w-9 h-9 rounded-lg bg-warm-secondary flex items-center justify-center text-latte-gold mb-4">
				<Icon className="w-4 h-4" />
			</div>

			<p
				className={`font-serif font-semibold text-espresso break-words ${
					compact ? "text-lg lg:text-xl" : "text-2xl lg:text-3xl"
				}`}
			>
				{value}
			</p>

			<p className="text-xs lg:text-sm text-soft-charcoal-secondary mt-1">
				{label}
			</p>
		</div>
	);
}

// ─────────────────────────────────────────────────────────────────
// Profile item
// ─────────────────────────────────────────────────────────────────

function ProfileItem({ icon: Icon, label, value }) {
	return (
		<div className="flex gap-3 min-w-0">
			<div className="w-9 h-9 rounded-lg bg-warm-secondary flex items-center justify-center text-latte-gold flex-shrink-0">
				<Icon className="w-4 h-4" />
			</div>

			<div className="min-w-0">
				<p className="text-xs text-soft-charcoal-secondary mb-1">{label}</p>

				<p className="text-sm font-medium text-espresso break-words">{value}</p>
			</div>
		</div>
	);
}

// ─────────────────────────────────────────────────────────────────
// Loading
// ─────────────────────────────────────────────────────────────────

function LoadingCard({ text }) {
	return (
		<div className="bg-white border border-border-subtle rounded-xl p-10 text-center">
			<div className="w-8 h-8 border-2 border-border-subtle border-t-latte-gold rounded-full animate-spin mx-auto mb-4" />

			<p className="text-sm text-soft-charcoal-secondary">{text}</p>
		</div>
	);
}

// ─────────────────────────────────────────────────────────────────
// Empty orders
// ─────────────────────────────────────────────────────────────────

function EmptyOrders() {
	return (
		<div className="bg-white border border-border-subtle rounded-xl p-8 lg:p-12 text-center">
			<div className="w-14 h-14 rounded-full bg-warm-secondary flex items-center justify-center mx-auto mb-5 text-latte-gold">
				<Package className="w-6 h-6" />
			</div>

			<h3 className="font-serif text-xl font-semibold text-espresso">
				Aucune commande
			</h3>

			<p className="text-soft-charcoal-secondary text-sm mt-2 mb-6 max-w-md mx-auto">
				Vous n'avez pas encore passé de commande. Découvrez notre sélection et
				passez votre première commande.
			</p>

			<Button asChild className="btn-primary">
				<Link to="/menu">
					Découvrir le menu
					<ChevronRight className="w-4 h-4 ml-2" />
				</Link>
			</Button>
		</div>
	);
}

// ─────────────────────────────────────────────────────────────────
// Order card
// ─────────────────────────────────────────────────────────────────

function OrderCard({
	order,
	formatPrice,
	formatDate,
	getStatusLabel,
	getStatusColor,
	featured = false,
}) {
	const shortId = order.id ? String(order.id).slice(0, 8).toUpperCase() : "—";

	return (
		<article
			className={`bg-white border border-border-subtle rounded-xl overflow-hidden transition-shadow hover:shadow-sm ${
				featured ? "lg:p-1" : ""
			}`}
			data-testid={`order-${order.id}`}
		>
			<div className="p-5 lg:p-6">
				<div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-5">
					<div className="min-w-0">
						<div className="flex flex-wrap items-center gap-3 mb-2">
							<p className="font-serif text-lg font-semibold text-espresso">
								Commande #{shortId}
							</p>

							<span
								className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border ${getStatusColor(
									order.status,
								)}`}
							>
								{getStatusLabel(order.status)}
							</span>
						</div>

						<div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-soft-charcoal-secondary">
							<span className="inline-flex items-center gap-1.5">
								<CalendarDays className="w-3.5 h-3.5" />
								{formatDate(order.created_at)}
							</span>

							{order.order_items?.length > 0 && (
								<span className="inline-flex items-center gap-1.5">
									<ShoppingBag className="w-3.5 h-3.5" />
									{order.order_items.reduce(
										(total, item) => total + Number(item.quantity || 0),
										0,
									)}{" "}
									article
									{order.order_items.reduce(
										(total, item) => total + Number(item.quantity || 0),
										0,
									) !== 1
										? "s"
										: ""}
								</span>
							)}
						</div>
					</div>

					<div className="flex items-center justify-between lg:justify-end gap-5 lg:text-right">
						<div>
							<p className="text-xs text-soft-charcoal-secondary">Total</p>

							<p className="font-serif text-lg font-semibold text-espresso mt-0.5">
								{formatPrice(order.total_amount)}
							</p>
						</div>

						<Button
							asChild
							variant="ghost"
							className="border border-border-subtle hover:bg-warm-secondary"
						>
							<Link to={`/commande/${order.id}`}>
								<span className="hidden sm:inline">Voir la commande</span>

								<ChevronRight className="w-4 h-4 sm:ml-2" />
							</Link>
						</Button>
					</div>
				</div>

				{order.order_items?.length ? (
					<div className="mt-5 pt-5 border-t border-border-subtle">
						<div className="space-y-2.5">
							{order.order_items
								.slice(0, featured ? 3 : undefined)
								.map((item) => (
									<div
										key={item.id}
										className="flex items-start justify-between gap-5 text-sm"
									>
										<span className="text-soft-charcoal min-w-0">
											{item.product_name}{" "}
											<span className="text-soft-charcoal-secondary">
												× {item.quantity}
											</span>
										</span>

										<span className="text-soft-charcoal-secondary whitespace-nowrap">
											{formatPrice(item.total_price)}
										</span>
									</div>
								))}

							{featured && order.order_items.length > 3 && (
								<p className="text-xs text-soft-charcoal-secondary pt-1">
									+{order.order_items.length - 3} autre
									{order.order_items.length - 3 > 1 ? "s" : ""} article
									{order.order_items.length - 3 > 1 ? "s" : ""}
								</p>
							)}
						</div>
					</div>
				) : (
					<div className="mt-5 pt-5 border-t border-border-subtle">
						<p className="text-sm text-soft-charcoal-secondary">
							Détails de commande indisponibles.
						</p>
					</div>
				)}
			</div>
		</article>
	);
}
